<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WidgetPreviewHost from '../components/WidgetPreviewHost.vue';
import {
  getWidgetLocaleMessages,
  readWidgetLocaleText,
} from 'dashboard/helper/widgetLocale';

const SCRIPT_OPEN = '\x3cscript\x3e';
const SCRIPT_CLOSE = '\x3c\x2fscript\x3e';

const ELEMENT_TYPES = [
  { value: 'thank_you', label: 'Thank you message', icon: '✓' },
  { value: 'question', label: 'Question', icon: '?' },
  { value: 'message', label: 'Message', icon: '≡' },
  { value: 'phone', label: 'Phone number', icon: '☎' },
  { value: 'choice_list', label: 'Choice list', icon: '◉' },
  { value: 'dropdown', label: 'Dropdown', icon: '▾' },
  { value: 'multiple_choice', label: 'Multiple choice list', icon: '☑' },
  { value: 'chat_rating', label: 'Chat rating', icon: '👍', disabled: false },
];

const DEFAULT_POST_CHAT_FORM_ELEMENTS = [
  {
    id: 'post_first_time',
    type: 'choice_list',
    label: 'Is this the first time you have chatted with us about this case?',
    required: true,
    choices: ['Yes', 'No'],
  },
  {
    id: 'post_resolved',
    type: 'choice_list',
    label: 'Was the case resolved during the chat?',
    required: true,
    choices: ['Yes', 'No'],
  },
  {
    id: 'post_rating',
    type: 'chat_rating',
    required: true,
    ratingLabel: 'How would you rate this chat?',
    commentLabel: '',
    ratingStyle: 'thumbs',
  },
];

const cloneDefaultPostChatElements = () =>
  DEFAULT_POST_CHAT_FORM_ELEMENTS.map(el => ({
    ...el,
    choices: Array.isArray(el.choices) ? [...el.choices] : el.choices,
  }));

let nextId = 1;

export default {
  name: 'PostChatFormSettings',
  components: { NextButton, WidgetPreviewHost },
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      formElements: [],
      showAddMenu: false,
      previewDocHtml: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    widgetLocaleMessages() {
      return getWidgetLocaleMessages(this.inbox.widget_language);
    },
    questionFieldPlaceholder() {
      return this.widgetLocaleText(
        'PRE_CHAT_FORM.FIELDS.MESSAGE.PLACEHOLDER',
        'Enter your question...'
      );
    },
    messageFieldPlaceholder() {
      return this.widgetLocaleText(
        'PRE_CHAT_FORM.FIELDS.MESSAGE.PLACEHOLDER',
        'Enter message text...'
      );
    },
    phoneFieldPlaceholder() {
      return this.widgetLocaleText(
        'PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.LABEL',
        'Phone number'
      );
    },
    ratingFieldPlaceholder() {
      return this.widgetLocaleText('CSAT.TITLE', 'How would you rate this chat?');
    },
    commentFieldPlaceholder() {
      return this.widgetLocaleText('CSAT.PLACEHOLDER', 'Leave a comment...');
    },
    thankYouFieldPlaceholder() {
      return this.widgetLocaleText(
        'CSAT.SUBMITTED_TITLE',
        'Thank you for your feedback!'
      );
    },
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    isCsatSurveyEnabled() {
      return !!this.inbox.csat_survey_enabled;
    },
    hasRatingElement() {
      return this.formElements.some(e => e.type === 'chat_rating');
    },
    availableElementTypes() {
      return ELEMENT_TYPES.map(t => ({
        ...t,
        disabled: t.value === 'chat_rating' && this.hasRatingElement,
      }));
    },
    previewScene() {
      return 'postchat-form';
    },
    liveChatwootSettings() {
      const existingTheme = this.inbox.widget_theme_config || {};
      return {
        locale: this.inbox.widget_language || 'en',
        position: this.inbox.position || 'right',
        type: 'standard',
        allowThemeOverride: true,
        darkMode:
          this.inbox.widget_theme_preset === 'midnight' ? 'dark' : 'light',
        themePreset: this.inbox.widget_theme_preset || 'classic',
        widgetStyle: 'rounded',
        widgetColor: this.inbox.widget_color || '#0059e1',
        iconColor: '#ffffff',
        themeConfig: {
          ...existingTheme,
          postChatForm: {
            enabled: true,
            formElements: this.formElements,
          },
        },
        welcomeTitle: this.inbox.welcome_title || '',
        welcomeDescription: this.inbox.welcome_tagline || '',
        announcementText: this.inbox.announcement_text || '',
        announcementUrl: this.inbox.announcement_url || '',
        announcementBgColor: this.inbox.announcement_bg_color || '#242529',
        announcementTextColor: this.inbox.announcement_text_color || '#ffffff',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        initialRoute: 'postchat-form',
        previewScene: this.previewScene,
        previewSceneData: {},
      };
    },
  },
  watch: {
    inbox() {
      this.loadFromInbox();
      this.previewDocHtml = this.buildPreviewDoc();
    },
    formElements: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
  },
  mounted() {
    this.loadFromInbox();
    this.previewDocHtml = this.buildPreviewDoc();
    document.addEventListener('click', this.handleGlobalClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick);
  },
  methods: {
    widgetLocaleText(path, fallback = '') {
      return readWidgetLocaleText(this.widgetLocaleMessages, path, fallback);
    },
    loadFromInbox() {
      const cfg = this.inbox.widget_theme_config || {};
      const saved = cfg.postChatForm?.formElements;
      if (Array.isArray(saved) && saved.length) {
        this.formElements = saved.map(e => ({
          ...e,
          ...(e.type === 'chat_rating' && !e.ratingStyle ? { ratingStyle: 'thumbs' } : {}),
        }));
      } else {
        // Default post-chat form (language-independent).
        this.formElements = cloneDefaultPostChatElements();
      }
    },
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const bg =
        isMidnight && this.inbox.widget_theme_config?.background
          ? this.inbox.widget_theme_config.background
          : isMidnight
            ? '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);
      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style>` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        `.woot--bubble-holder{visibility:hidden;transition:visibility 0s;}` +
        `</style>` +
        `</head><body>` +
        SCRIPT_OPEN +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{locale:(d.s&&d.s.locale)||'en',showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);};` +
        `})(document,"script");` +
        SCRIPT_CLOSE +
        `</body></html>`
      );
    },
    handleGlobalClick(e) {
      if (!this.showAddMenu) return;
      if (!e.target.closest('.pcf-add-wrap')) {
        this.showAddMenu = false;
      }
    },
    addElement(type) {
      if (type === 'chat_rating' && this.hasRatingElement) return;
      const base = { id: `el_${nextId++}`, type, required: false };
      if (
        type === 'choice_list' ||
        type === 'dropdown' ||
        type === 'multiple_choice'
      ) {
        this.formElements.push({
          ...base,
          label: '',
          choices: ['', ''],
        });
      } else if (type === 'chat_rating') {
        this.formElements.push({
          ...base,
          ratingLabel: this.ratingFieldPlaceholder,
          commentLabel: '',
          ratingStyle: 'thumbs',
        });
      } else if (type === 'thank_you') {
        this.formElements.push({
          ...base,
          message: this.thankYouFieldPlaceholder,
        });
      } else if (type === 'phone') {
        this.formElements.push({
          ...base,
          label: this.phoneFieldPlaceholder,
        });
      } else {
        this.formElements.push({ ...base, label: '' });
      }
      this.showAddMenu = false;
    },
    removeElement(id) {
      this.formElements = this.formElements.filter(e => e.id !== id);
    },
    addChoice(element) {
      element.choices.push('');
    },
    removeChoice(element, index) {
      element.choices.splice(index, 1);
    },
    elementTypeLabel(type) {
      const found = ELEMENT_TYPES.find(t => t.value === type);
      return found ? found.label : type;
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe?.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (_) {}
    },
    openWebsitePreview() {
      const url =
        this.inbox.website_url || this.inbox.channel?.website_url || '';
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    cancel() {
      this.loadFromInbox();
    },
    async save() {
      try {
        const existingThemeCfg = this.inbox.widget_theme_config || {};
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: {
            widget_theme_config: {
              ...existingThemeCfg,
              postChatForm: {
                enabled: true,
                formElements: this.formElements,
              },
            },
          },
        });
        useAlert('Post-chat form saved');
      } catch (e) {
        useAlert('Unable to save post-chat form');
      }
    },
  },
};
</script>

<template>
  <div class="pcf-page">
    <div class="pcf-layout">
      <!-- ═══════════════════════════════════════════════
           LEFT PANEL: Settings
           ═══════════════════════════════════════════════ -->
      <div class="pcf-settings-panel">
        <!-- Header bar (56px, white, border-bottom) -->
        <div class="pcf-header-bar">
          <div class="pcf-header-title">Post-chat form</div>
        </div>

        <!-- Scrollable content area -->
        <div class="pcf-content-scroll">
          <div class="pcf-content-inner">
            <!-- CSAT conflict warning -->
            <div
              v-if="isCsatSurveyEnabled"
              class="mb-3 flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300"
            >
              <span class="mt-0.5 shrink-0">⚠️</span>
              <span>CSAT Survey is also enabled. Customers will see two separate rating systems. Consider disabling CSAT Survey if you are using Post-chat form instead.</span>
            </div>

            <!-- Description -->
            <p class="pcf-desc">
              Collect feedback after a conversation ends. Add questions,
              ratings, and more.
            </p>

            <!-- Section label -->
            <div class="pcf-section-label">Customize form</div>

            <!-- Form elements list -->
            <div class="pcf-cards-area">
              <!-- Each element card -->
              <div
                v-for="el in formElements"
                :key="el.id"
                class="pf-element-card"
              >
                <!-- Card header -->
                <div class="pf-element-header">
                  <span class="pf-element-type-label">{{
                    elementTypeLabel(el.type)
                  }}</span>
                  <div class="pf-element-header-right">
                    <label
                      v-if="el.type !== 'thank_you'"
                      class="pf-required-label"
                    >
                      <input
                        v-model="el.required"
                        type="checkbox"
                        class="pf-required-check"
                      />
                      Required
                    </label>
                    <button
                      class="pf-delete-btn"
                      type="button"
                      aria-label="Delete"
                      @click="removeElement(el.id)"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Choice list / Dropdown / Multiple choice -->
                <template
                  v-if="
                    ['choice_list', 'dropdown', 'multiple_choice'].includes(
                      el.type
                    )
                  "
                >
                  <div class="pf-field">
                    <label class="pf-label">Label</label>
                    <input
                      v-model="el.label"
                      class="pf-input"
                      :placeholder="questionFieldPlaceholder"
                    />
                  </div>
                  <div class="pf-choices">
                    <div
                      v-for="(choice, ci) in el.choices"
                      :key="ci"
                      class="pf-choice-row"
                    >
                      <input
                        v-model="el.choices[ci]"
                        class="pf-input pf-input--choice"
                        :placeholder="`Choice #${ci + 1}`"
                      />
                      <button
                        v-if="el.choices.length > 1"
                        class="pf-remove-choice"
                        type="button"
                        aria-label="Remove choice"
                        @click="removeChoice(el, ci)"
                      >
                        ✕
                      </button>
                    </div>
                    <button
                      class="pf-add-choice-btn"
                      type="button"
                      @click="addChoice(el)"
                    >
                      + Add answer
                    </button>
                  </div>
                </template>

                <!-- Chat rating -->
                <template v-else-if="el.type === 'chat_rating'">
                  <div class="pf-field">
                    <label class="pf-label">Rating style</label>
                    <div class="pf-rating-style-group">
                      <label class="pf-rating-style-option">
                        <input
                          v-model="el.ratingStyle"
                          type="radio"
                          value="thumbs"
                          class="pf-rating-style-radio"
                        />
                        <span class="pf-rating-style-label">👍👎 Thumbs up / down</span>
                      </label>
                      <label class="pf-rating-style-option">
                        <input
                          v-model="el.ratingStyle"
                          type="radio"
                          value="emoji"
                          class="pf-rating-style-radio"
                        />
                        <span class="pf-rating-style-label">😞😑😐😀😍 Emoji (5 levels)</span>
                      </label>
                    </div>
                  </div>
                  <div class="pf-field" style="margin-top: 12px">
                    <label class="pf-label">Rating label</label>
                    <input
                      v-model="el.ratingLabel"
                      class="pf-input"
                      :placeholder="ratingFieldPlaceholder"
                    />
                  </div>
                  <div class="pf-field" style="margin-top: 12px">
                    <label class="pf-label">Comment label</label>
                    <textarea
                      v-model="el.commentLabel"
                      class="pf-textarea"
                      rows="2"
                      :placeholder="commentFieldPlaceholder"
                    />
                  </div>
                  <!-- Rating preview -->
                  <div class="pf-rating-preview">
                    <template v-if="el.ratingStyle === 'emoji'">
                      <span
                        v-for="emoji in ['😞','😑','😐','😀','😍']"
                        :key="emoji"
                        class="pf-rating-emoji"
                      >{{ emoji }}</span>
                    </template>
                    <template v-else>
                      <span class="pf-rating-thumb" title="Good">
                        <svg
                          viewBox="0 0 32 32"
                          width="22"
                          height="22"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          aria-hidden="true"
                        >
                          <path
                            d="M18.1551,7.38923207 L14.4544,12.2479321 C14.1237,12.5724321 13.9359,13.0197321 13.9359,13.4845321 L13.9359,22.2460321 C13.9359,23.2107321 14.7404,24.0000321 15.7237,24.0000321 L20.7295,24.0000321 C21.4446,24.0000321 22.0882,23.5790321 22.3653,22.9388321 L25.2794,16.2647321 C26.0392,14.5282321 24.743,12.5987321 22.8212,12.5987321 L19.5585,12.5987321 L20.4077,8.58193207 C20.4971,8.14343207 20.363,7.69613207 20.0412,7.38043207 C19.5138,6.87173207 18.6735,6.87173207 18.1551,7.38923207 Z M9.2877,24.0000321 C10.271,24.0000321 11.0755,23.2107321 11.0755,22.2460321 L11.0755,15.2298321 C11.0755,14.2650321 10.271,13.4757321 9.2877,13.4757321 C8.3045,13.4757321 7.5,14.2650321 7.5,15.2298321 L7.5,22.2460321 C7.5,23.2107321 8.3045,24.0000321 9.2877,24.0000321 Z"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                      <span class="pf-rating-thumb" title="Bad">
                        <svg
                          viewBox="0 0 32 32"
                          width="22"
                          height="22"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          aria-hidden="true"
                        >
                          <path
                            d="M17.1551,25.6108 L13.4544,20.7521 C13.1237,20.4276 12.9359,19.9803 12.9359,19.5155 L12.9359,10.754 C12.9359,9.7893 13.7404,9 14.7237,9 L19.7295,9 C20.4446,9 21.0882,9.421 21.3653,10.0612 L24.2794,16.7353 C25.0392,18.4718 23.743,20.4013 21.8212,20.4013 L18.5585,20.4013 L19.4077,24.4181 C19.4971,24.8566 19.363,25.3039 19.0412,25.6196 C18.5138,26.1283 17.6735,26.1283 17.1551,25.6108 Z M8.2877,9 C9.271,9 10.0755,9.7893 10.0755,10.754 L10.0755,17.7702 C10.0755,18.735 9.271,19.5243 8.2877,19.5243 C7.3045,19.5243 6.5,18.735 6.5,17.7702 L6.5,10.754 C6.5,9.7893 7.3045,9 8.2877,9 Z"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                    </template>
                  </div>
                </template>

                <!-- Thank you message -->
                <template v-else-if="el.type === 'thank_you'">
                  <div class="pf-field">
                    <label class="pf-label">Message</label>
                    <textarea
                      v-model="el.message"
                      class="pf-textarea"
                      rows="2"
                      :placeholder="thankYouFieldPlaceholder"
                    />
                  </div>
                </template>

                <!-- Question / Message / Phone number -->
                <template v-else>
                  <div class="pf-field">
                    <label class="pf-label">{{
                      el.type === 'question'
                        ? 'Question'
                        : el.type === 'phone'
                          ? 'Phone number'
                          : 'Message'
                    }}</label>
                    <input
                      v-model="el.label"
                      class="pf-input"
                      :type="el.type === 'phone' ? 'tel' : 'text'"
                      :placeholder="
                        el.type === 'question'
                          ? questionFieldPlaceholder
                          : el.type === 'phone'
                            ? phoneFieldPlaceholder
                            : messageFieldPlaceholder
                      "
                    />
                  </div>
                </template>
              </div>

              <!-- Add element button + dropdown menu -->
              <div class="pcf-add-wrap">
                <button
                  class="pcf-add-btn"
                  type="button"
                  @click.stop="showAddMenu = !showAddMenu"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add element
                </button>

                <transition name="pcf-menu-fade">
                  <div v-if="showAddMenu" class="pcf-add-menu" @click.stop>
                    <button
                      v-for="t in availableElementTypes"
                      :key="t.value"
                      class="pcf-add-menu-item"
                      :class="{ 'pcf-add-menu-item--disabled': t.disabled }"
                      type="button"
                      :disabled="t.disabled"
                      @click="addElement(t.value)"
                    >
                      <span class="pcf-add-menu-icon">{{ t.icon }}</span>
                      <span>{{ t.label }}</span>
                    </button>
                  </div>
                </transition>
              </div>
            </div>
            <!-- /pcf-cards-area -->
          </div>
          <!-- /pcf-content-inner -->
        </div>
        <!-- /pcf-content-scroll -->

        <!-- Sticky save bar -->
        <div class="pcf-save-bar">
          <button class="pcf-cancel-btn" type="button" @click="cancel">
            Cancel
          </button>
          <NextButton
            class="pcf-save-btn"
            type="button"
            label="Save changes"
            :is-loading="uiFlags.isUpdating"
            :disabled="uiFlags.isUpdating"
            @click="save"
          />
        </div>
      </div>
      <!-- /pcf-settings-panel -->

      <!-- ═══════════════════════════════════════════════
           RIGHT PANEL: Preview
           ═══════════════════════════════════════════════ -->
      <div class="pcf-preview-panel">
        <!-- Preview header bar (gray, 56px) -->
        <div class="pcf-preview-header-bar">
          <div class="pcf-preview-title">Preview</div>
        </div>

        <!-- Preview content area -->
        <div class="pcf-preview-content-area">
          <div class="pcf-preview-scroll-inner">
            <div class="pcf-preview-widget-holder">
              <div class="pcf-preview-widget-stage">
                <div class="pcf-preview-widget-inner">
                  <WidgetPreviewHost
                    :inbox="inbox"
                    :settings="liveChatwootSettings"
                    iframe-class="pcf-preview-iframe"
                    empty-class="pcf-preview-empty"
                  />
                </div>
              </div>
            </div>
            <p class="pcf-preview-test-note">
              <a href="#" @click.prevent="openWebsitePreview">Test it out</a> on
              the chat page
            </p>
          </div>
        </div>
      </div>
      <!-- /pcf-preview-panel -->
    </div>
    <!-- /pcf-layout -->
  </div>
  <!-- /pcf-page -->
</template>

<style scoped>
/* ══════════════════════════════════════════════════════
   Root & layout — mirrors LanguageSettings/PreChatForm
   ══════════════════════════════════════════════════════ */
.pcf-page {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.pcf-layout {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* LEFT PANEL */
.pcf-settings-panel {
  flex: 5 1 62.5%;
  min-width: 296px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-sizing: border-box;
}

/* Header bar */
.pcf-header-bar {
  display: flex;
  align-items: center;
  width: 100%;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
  box-sizing: border-box;
  padding-left: 21px;
}

.pcf-header-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
}

/* Scrollable content */
.pcf-content-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.pcf-content-inner {
  padding: 24px 32px 80px;
  box-sizing: border-box;
  max-width: 960px;
}

/* Description */
.pcf-desc {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
}

/* Section label */
.pcf-section-label {
  line-height: 24px;
  color: #111827;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 15px;
}

/* Cards area */
.pcf-cards-area {
  max-width: 705px;
}

/* ── Element cards ── */
.pf-element-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: #fff;
  width: 100%;
  max-width: 705px;
  overflow: hidden;
  margin-bottom: 12px;
  padding: 14px 16px;
  box-sizing: border-box;
}

.pf-element-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.pf-element-type-label {
  font-size: 13px;
  font-weight: 700;
  color: #000;
}
.pf-element-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.pf-required-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  white-space: nowrap;
}
.pf-required-check {
  cursor: pointer;
  accent-color: #4384f5;
}
.pf-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: transparent;
  color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  padding: 0;
  transition:
    color 0.15s,
    background 0.15s;
}
.pf-delete-btn:hover {
  background: rgba(229, 70, 102, 0.08);
  color: #e54666;
  border-color: rgba(229, 70, 102, 0.3);
}

.pf-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.pf-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
}

.pf-input {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  background: #fff;
  color: #000;
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  outline: none;
  font-family: inherit;
}
.pf-input:focus {
  border-color: rgba(67, 132, 245, 0.55);
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.14);
}
.pf-textarea {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  background: #fff;
  color: #000;
  width: 100%;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  line-height: 1.5;
  font-family: inherit;
}
.pf-textarea:focus {
  border-color: rgba(67, 132, 245, 0.55);
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.14);
}

/* Choices */
.pf-choices {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pf-choice-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pf-input--choice {
  flex: 1;
}
.pf-remove-choice {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  background: transparent;
  color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pf-remove-choice:hover {
  background: rgba(229, 70, 102, 0.08);
  color: #e54666;
}
.pf-add-choice-btn {
  align-self: flex-start;
  border: none;
  background: transparent;
  color: #4384f5;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
}

/* Rating */
.pf-rating-preview {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.pf-rating-thumb {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 50%;
  color: rgba(17, 17, 17, 0.75);
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.pf-rating-emoji {
  font-size: 22px;
  line-height: 1;
  cursor: default;
}

.pf-rating-style-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.pf-rating-style-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.pf-rating-style-radio {
  cursor: pointer;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.pf-rating-style-label {
  font-size: 13px;
  color: var(--color-body, #111);
}

/* Add element button + menu */
.pcf-add-wrap {
  position: relative;
  max-width: 705px;
  margin-bottom: 24px;
}
.pcf-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 16px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 6px;
  background: #fff;
  color: #000;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.pcf-add-btn:hover {
  background: rgba(0, 0, 0, 0.04);
}

.pcf-add-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 999;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  min-width: 200px;
  overflow: hidden;
  padding: 4px 0;
}
.pcf-add-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #000;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}
.pcf-add-menu-item:hover:not(.pcf-add-menu-item--disabled) {
  background: rgba(67, 132, 245, 0.07);
}
.pcf-add-menu-item--disabled {
  opacity: 0.38;
  cursor: not-allowed;
}
.pcf-add-menu-icon {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.5);
  width: 18px;
  text-align: center;
}

/* Menu animation */
.pcf-menu-fade-enter-active,
.pcf-menu-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.pcf-menu-fade-enter-from,
.pcf-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Save bar (sticky bottom) ── */
.pcf-save-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  background: #fff;
  flex-shrink: 0;
}

.pcf-cancel-btn {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #bcc6d0;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
}
.pcf-cancel-btn:hover {
  background: #f5f5f5;
}

.pcf-save-bar :deep(.pcf-save-btn) {
  height: 36px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  background: #0059e1 !important;
  border: 1px solid #0059e1 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.pcf-save-bar :deep(.pcf-save-btn:hover) {
  background: #0047c2 !important;
  border-color: #0047c2 !important;
}

/* ══════════════════════════════════════════════════════
   RIGHT PANEL (Preview) — mirrors Language/PreChatForm
   ══════════════════════════════════════════════════════ */
.pcf-preview-panel {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.pcf-preview-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.pcf-preview-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  margin-left: 20px;
}

.pcf-preview-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;
}

.pcf-preview-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.pcf-preview-widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.pcf-preview-widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.pcf-preview-widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

.pcf-preview-iframe {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.pcf-preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

.pcf-preview-test-note {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 8px 0 16px;
}

.pcf-preview-test-note a {
  color: #0059e1;
  text-decoration: none;
}

.pcf-preview-test-note a:hover {
  text-decoration: underline;
}
</style>
